import { CheckIcon, ClockIcon } from "@phosphor-icons/react/dist/ssr";
import {
  BUILDS,
  buildMethods,
  buildHref,
  getBuild,
  prefetchTutorial,
  tutorialHref,
} from "./data.js";
import { PickerCard } from "./PickerCard.js";

export function BuildPicker() {
  const builds = [...BUILDS].sort(
    (a, b) => Number(b.featured ?? false) - Number(a.featured ?? false),
  );
  return (
    <div className="not-prose grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {builds.map((build) => (
        <PickerCard
          key={build.id}
          href={buildHref(build.id)}
          icon={build.icon}
          label={build.label}
          desc={build.desc}
          time={build.time}
          featured={build.featured}
          color={build.color}
          cta={
            buildMethods(build.id).length === 1
              ? "Start tutorial"
              : "Choose workflow"
          }
        />
      ))}
    </div>
  );
}

export function MethodPicker({ build: buildId }: { build: string }) {
  const build = getBuild(buildId);
  if (!build) throw new Error(`Unknown build "${buildId}"`);
  return (
    <div className="not-prose mt-8 max-w-6xl">
      <section aria-labelledby="choose-workflow">
        <h2
          id="choose-workflow"
          className="!m-0 text-xl font-semibold tracking-tight text-foreground"
        >
          Choose a workflow to start
        </h2>
        <p className="mb-5 mt-2 text-sm leading-relaxed text-muted-foreground">
          Pick one of these three options to open your tutorial. You can switch
          workflows later in the guide.
        </p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {buildMethods(build.id).map((method) => (
            <PickerCard
              key={method.id}
              href={tutorialHref(build.id, method.id)}
              icon={method.icon}
              label={method.label}
              desc={method.desc}
              needs={method.needs}
              cta={method.cta}
              color={build.color}
              onIntent={() => prefetchTutorial(build.id, method.id)}
            />
          ))}
        </div>
      </section>
      <section
        aria-labelledby="tutorial-outcome"
        className="mt-8 border-t border-border pt-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2
            id="tutorial-outcome"
            className="!m-0 text-base font-semibold text-foreground"
          >
            What you'll build
          </h2>
          <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <ClockIcon aria-hidden="true" size={15} />
            About {build.time.replace("~", "")} for the tutorial
          </span>
        </div>
        <p className="!mb-5 !mt-2 text-sm leading-relaxed text-muted-foreground">
          {build.summary}
        </p>
        <ul className="m-0 grid list-none gap-4 p-0 lg:grid-cols-3">
          {build.youll.map((outcome) => (
            <li
              key={outcome}
              className="flex gap-2 text-sm leading-relaxed text-muted-foreground"
            >
              <CheckIcon
                aria-hidden="true"
                size={16}
                className="mt-0.5 shrink-0"
              />
              <span>{outcome}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
